import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { vars, breakpoints, Heading, Inline } from '@etg/wings';
import { Collection, DevBar } from '@eti/debug';
import { useSiteContext, useProperty, useSearch } from '@eti/providers';
import { css, cx } from '@eti/styles';
import { SearchLocation, SelfServiceRebookingTraveler } from '@eti/types';
import { Profiler } from '@eti/utils';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { SearchForm as LegacySearchForm, searchForm } from '../../widgets/search-form';
import { routes } from '../../constants/routesAndApisConstants';
import { DebugCommonComponents } from '../debug/components';
import { isDev } from '../debug/utils';
import { SEARCH } from '../../constants/formNamesConstants';
import { getSearchFormDataFrom } from '../../widgets/search-form/utils/initialValues';
import SearchFormNewContainer from './SearchFormNewContainer';
import SearchFormCard from './SearchFormCard';
import SearchFormSection from './SearchFormSection';

const contentStyles = css`
  display: flex;
  flex: 1;
  justify-content: center;
  max-width: 26.25rem;
  text-align: center;
`;

const headingStyles = css`
  color: ${vars.colors.panel.foreground};

  @media (min-width: ${breakpoints._768}) {
    color: #fff;
    text-shadow: 0 0 10px #000;
  }
`;

const carrierLogoStyles = css`
  height: 100px;
`;

const SearchFormPanel = ({
  carrierLogoUrl,
  destination,
  origin,
  panelHeaderTitle,
  rebookingData = null,
  setValueRef,
}) => {
  const history = useHistory();
  const { language } = useSiteContext();
  const { p } = useProperty();
  const { currentSearchData } = useSearch();
  const search = getSearchFormDataFrom(currentSearchData);

  useEffect(() => {
    const loadResultPage = async () => {
      if (origin?.value && destination?.value) {
        const ResultPage = await import('../../pages/result');
        ResultPage.default.preload();
      }
    };

    loadResultPage();
  }, [origin, destination]);

  const handleSearch = (searchParameters) => {
    sessionStorage.removeItem('hasPaymentFailed');

    if (p('IbeClient.RedirectCustomerToAnotherSite.Enabled')) {
      history.push(routes.NEW_BRAND_REDIRECTION, { searchVariables: searchParameters });
    } else {
      history.push(routes.RESULT, { searchVariables: searchParameters });
    }
  };

  const isStartPage =
    history?.location?.pathname === routes.HOME || history?.location?.pathname === routes.START;

  const SearchForm = p('IbeClient.ReactHookForm.SearchForm.Enabled')
    ? SearchFormNewContainer
    : LegacySearchForm;

  return (
    <>
      <SearchFormSection>
        <Inline
          align="center"
          alignY="center"
          collapseBelow={breakpoints._768}
          spacing={{ [breakpoints._0]: 24, [breakpoints._768]: 32 }}
        >
          <Heading
            className={cx(headingStyles, contentStyles)}
            data-testid="searchFormPanel-header"
            level={1}
          >
            {panelHeaderTitle}
          </Heading>
          {carrierLogoUrl && (
            <div className={contentStyles}>
              <img
                alt={panelHeaderTitle}
                className={carrierLogoStyles}
                data-testid="searchForm-carrierLogo-image"
                src={carrierLogoUrl}
              />
            </div>
          )}
        </Inline>
        <SearchFormCard data-testid="searchPage-searchForm">
          <Profiler>
            <SearchForm
              isStartPage={isStartPage}
              language={language.code}
              onSearch={handleSearch}
              rebookingData={rebookingData}
              search={search}
              setValueRef={setValueRef}
              shouldShowRebookingBanner
            />
          </Profiler>
        </SearchFormCard>
      </SearchFormSection>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

const mapStateToProps = (state, { destination, origin }) => {
  const searchProps = { ...searchForm.getSingleBound(SEARCH)(state) };
  return {
    ...searchProps,
    destination: destination || searchProps.destination,
    origin: origin || searchProps.origin,
  };
};

SearchFormPanel.propTypes = {
  carrierLogoUrl: PropTypes.string,
  destination: SearchLocation,
  origin: SearchLocation,
  panelHeaderTitle: PropTypes.string,
  rebookingData: PropTypes.shape({
    criteria: PropTypes.arrayOf(PropTypes.string),
    formConstraints: PropTypes.shape({
      passengers: PropTypes.shape({
        isDisabled: PropTypes.bool,
        maxNumberOfTravelers: PropTypes.number,
      }),
    }),
    initialValues: PropTypes.shape({
      passengers: PropTypes.shape({
        adults: PropTypes.number,
        children: PropTypes.number,
        infants: PropTypes.number,
      }),
    }),
    travelers: PropTypes.arrayOf(SelfServiceRebookingTraveler),
  }),
  setValueRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
};

export default connect(mapStateToProps)(SearchFormPanel);
